import { Button } from 'components/Button';
import { Grid, GridProps } from 'components/Grid/Grid';
import { Icon, ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { Txt } from 'components/Txt';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import React from 'react';
import { FlatIssueType } from 'types/DTOs/BlockManagerDTOs';
import { StyleSheet, View } from 'react-native';
import Animated, {
  FadeInDown,
  LinearTransition,
} from 'react-native-reanimated';
import { useDispatch } from 'react-redux';
import {
  getFlatContactConfirmationInfo,
  getFormattedFlatName,
} from 'store/remote/engageUtils';
import {
  doEngageFlatLoad,
  doEngageFlatsClearData,
  doEngageFlatsLoad,
  doEngageOnboard,
} from 'store/remote/engageActions';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  selectEngageFlatsFilterSearch,
  selectEnagageFlatsFilterissueContactTypes,
  selectEngageFlatsItem,
  selectEngageFlatsItemIds,
  selectEngageFlatsTotalCount,
} from 'store/remote/engageSelectors';
import { useSelector } from 'hooks/useSelector';
import { EngageLoader } from './EngageLoader';
import { ReferenceToText } from 'components/ReferenceText';
import { cloneDeep, sortBy } from 'lodash';
import { EngageFlatsHeader } from './EngageFlatsHeader';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { ProgressBar } from 'components/Visuals/ProgressBar';
import {
  EngageFlatListItem,
  FlatContactType,
  FlatContactTypes,
  FlatOccupationStatus,
} from 'types/Engage';
import { Pressable } from 'components/Pressable';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.Engage],
  Screens.EngageFlats
>;

type DefinedGridProps = GridProps<
  'flat' | 'contactTypes' | 'issues' | 'counts' | 'contactHasSupportNeeds',
  string,
  {
    pageKey: string;
    onRowPress: (property: EngageFlatListItem) => void;
  }
>;

const flatContactTypesCountTemplate = Object.values(FlatContactTypes).reduce(
  (acc, contactType) => {
    acc[contactType.id] = {
      name: contactType.name,
      namePlural: contactType.namePlural,
      ordering: contactType.ordering,
      count: 0,
    };
    return acc;
  },
  {} as Record<
    FlatContactType,
    { name: string; namePlural: string; ordering: number; count: number }
  >,
);

const useGetRowData: DefinedGridProps['getRowData'] = ({ id, extraData }) => {
  const { pageKey, onRowPress } = extraData;
  const data = useSelector((state) =>
    selectEngageFlatsItem(state, pageKey, id),
  );
  const searchValue = useSelector((state) =>
    selectEngageFlatsFilterSearch(state, pageKey),
  );
  const issueContactTypes = useSelector((state) =>
    selectEnagageFlatsFilterissueContactTypes(state, pageKey),
  );
  const [isContactsExpanded, setIsContactsExpanded] = React.useState(false);

  const matchingContactIds = React.useMemo(
    () => data?.matchingContactIds ?? [],
    [data?.matchingContactIds],
  );
  const hasMatchingContacts = matchingContactIds.length > 0;
  const hasEmailIssuesFilter = issueContactTypes.includes(
    FlatIssueType.EMAIL_ISSUES,
  );

  React.useEffect(() => {
    if (!data) {
      setIsContactsExpanded(false);
      return;
    }
    const trimmedSearch = (searchValue || '').trim();
    const shouldExpand =
      hasMatchingContacts && (trimmedSearch.length > 0 || hasEmailIssuesFilter);
    setIsContactsExpanded(shouldExpand);
  }, [data, hasEmailIssuesFilter, hasMatchingContacts, searchValue]);

  const handleToggleContacts = React.useCallback(() => {
    setIsContactsExpanded((prev) => !prev);
  }, []);

  return React.useMemo(() => {
    if (!data) return null;

    return {
      onPress: () => onRowPress(data),
      columns: {
        flat: () => (
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              columnGap: 0,
            }}
          >
            <Txt
              numberOfLines={1}
              weight="bold"
              size="large"
              style={{ flex: 1, minWidth: 0 }}
            >
              {getFormattedFlatName(data.name)}
            </Txt>
            {!!data.customReference && (
              <View
                style={{
                  // marginLeft: theme.paddingHorizontalHalf,
                  flexShrink: 1,
                  minWidth: 0,
                }}
              >
                <ReferenceToText customReference={data.customReference} />
              </View>
            )}
          </View>
        ),
        contactTypes: () => {
          const thisContactTypes = data.contacts.reduce((acc, contact) => {
            acc[contact.contactType].count += 1;
            return acc;
          }, cloneDeep(flatContactTypesCountTemplate));

          const residentsCount =
            thisContactTypes[FlatContactType.resident].count +
            thisContactTypes[FlatContactType.residentOwner].count;

          const thisContactTypesFilteredAndOrdered = sortBy(
            Object.values(thisContactTypes).filter((c) => !!c.count),
            ['ordering'],
          );

          const contactSummaryText = thisContactTypesFilteredAndOrdered
            .map((contactType) =>
              pluralise(
                contactType.count,
                `{x} ${contactType.name}`,
                `{x} ${contactType.namePlural}`,
              ),
            )
            .join(', ');

          const hasContacts = data.contacts.length > 0;

          const expandedContactsComponent = isContactsExpanded && (
            <Animated.View
              style={styles.contactList}
              entering={FadeInDown.duration(250)}
              layout={LinearTransition.duration(250)}
            >
              {data.contacts.map((contact, index) => {
                const isMatchingContact = matchingContactIds.includes(
                  contact.id,
                );
                return (
                  <Animated.View
                    key={contact.id}
                    style={[isMatchingContact && styles.matchingContactItem]}
                    entering={FadeInDown.duration(250).delay(index * 40)}
                    layout={LinearTransition.duration(250)}
                  >
                    <Txt
                      numberOfLines={1}
                      size="xxsmall"
                      style={[isMatchingContact && styles.matchingContactName]}
                    >
                      <Txt weight="bold" size="xsmall">
                        {contact.name}
                      </Txt>
                      {' • '}
                      <Txt size="xxsmall">
                        {FlatContactTypes[contact.contactType].name}
                      </Txt>
                    </Txt>
                  </Animated.View>
                );
              })}
            </Animated.View>
          );

          const occupationStatusIcon =
            data.occupationStatus === FlatOccupationStatus.OCCUPIED ? (
              <Icon
                source={ICONS.HouseOccupationOccupied}
                size={22}
                color={residentsCount === 0 ? theme.colors.red : undefined}
              />
            ) : data.occupationStatus === FlatOccupationStatus.UNKNOWN ? (
              <Icon source={ICONS.HouseOccupationUnknown} size={22} />
            ) : (
              <Icon source={ICONS.HouseOccupationUnoccupied} size={22} />
            );

          const occupationStatusText =
            data.occupationStatus === FlatOccupationStatus.OCCUPIED
              ? 'Occupied' +
                (residentsCount === 0 ? ' (Residents Not Known)' : ``)
              : data.occupationStatus === FlatOccupationStatus.UNKNOWN
                ? 'Occupation Not Known'
                : 'Unoccupied';

          return (
            <View
              style={{
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                columnGap: theme.paddingHorizontal,
              }}
            >
              {occupationStatusIcon}
              <View style={{ flex: 1 }}>
                {isContactsExpanded ? (
                  expandedContactsComponent
                ) : (
                  <>
                    <Txt size="xsmall" weight="bold" numberOfLines={1}>
                      {occupationStatusText}
                    </Txt>
                    <Txt size="xxsmall" numberOfLines={2}>
                      {contactSummaryText}
                    </Txt>
                  </>
                )}
              </View>
              {hasContacts && (
                <Pressable
                  style={{ alignSelf: 'stretch', justifyContent: 'center' }}
                  onPress={handleToggleContacts}
                >
                  <IconButton
                    icon={ICONS.CaretDownSolid}
                    onPress={handleToggleContacts}
                    imageStyle={{
                      transform: [
                        { rotate: isContactsExpanded ? '180deg' : '0deg' },
                      ],
                    }}
                  />
                </Pressable>
              )}
            </View>
          );
        },
        issues: () => {
          const hasOwnerIssues = Boolean(data.issues?.hasOwnerIssues);
          const hasResidentIssues = Boolean(data.issues?.hasResidentIssues);
          const hasEmailIssues = Boolean(data.issues?.hasEmailIssues);
          const hasIssues =
            hasOwnerIssues || hasResidentIssues || hasEmailIssues;
          const issueLabels = [
            hasOwnerIssues ? 'Owner Issues' : null,
            hasResidentIssues ? 'Resident Issues' : null,
            hasEmailIssues ? 'Email Issues' : null,
          ].filter(Boolean);

          return (
            <View
              style={{
                flex: 1,
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  columnGap: theme.paddingHorizontalHalf,
                }}
              >
                <View style={{ flex: 1, rowGap: theme.paddingHorizontalHalf }}>
                  {hasIssues ? (
                    issueLabels.map((label) => (
                      <Txt
                        key={label}
                        weight="bold"
                        size="small"
                        style={{ color: theme.colors.red }}
                      >
                        {label}
                      </Txt>
                    ))
                  ) : (
                    <Txt
                      weight="bold"
                      size="small"
                      style={{ color: theme.colors.green }}
                    >
                      No issues
                    </Txt>
                  )}
                </View>
              </View>
            </View>
          );
        },
        counts: () => {
          const contactsTotal = data.contacts.length;
          const contactsConfirmed = data.contacts.filter(
            (c) => getFlatContactConfirmationInfo(c).confirmedAndInDate,
          ).length;
          return (
            <View style={{ flex: 1, justifyContent: 'center' }}>
              <ProgressBar
                barValue={contactsConfirmed}
                barTotal={contactsTotal}
                barValueMsg="Confirmed"
                barEmptyMsg="No confirmed contacts"
              />
            </View>
          );
        },
        contactHasSupportNeeds: () => {
          const requiredSupportCount = data.contacts.filter(
            (contact) => contact.hasSupportNeeds,
          ).length;
          return (
            <View
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <View
                style={{
                  backgroundColor: requiredSupportCount
                    ? theme.colors.red
                    : undefined,
                  width: 22,
                  height: 22,
                  borderRadius: 100,
                }}
              >
                <Txt
                  style={{
                    textAlign: 'center',
                    color: requiredSupportCount
                      ? theme.colors.white
                      : undefined,
                  }}
                  weight="bold"
                >
                  {requiredSupportCount}
                </Txt>
              </View>
            </View>
          );
        },
      },
    };
  }, [
    data,
    handleToggleContacts,
    matchingContactIds,
    isContactsExpanded,
    onRowPress,
  ]);
};

export const EngageFlats: React.FC<Props> = ({
  navigation,
  route: {
    params: { pageKey, property },
  },
}) => {
  const dispatch = useDispatch();

  const gridIds = useSelector((state) =>
    selectEngageFlatsItemIds(state, pageKey),
  );

  const totalCount = useSelector((state) =>
    selectEngageFlatsTotalCount(state, pageKey),
  );
  const remainingCount = totalCount - gridIds.length;
  const [selectedFlatIds, setSelectedFlatIds] = React.useState<string[]>([]);
  const hasBulkSelection = selectedFlatIds.length > 0;
  React.useEffect(() => {
    setSelectedFlatIds((current) =>
      current.filter((id) => gridIds.includes(id)),
    );
  }, [gridIds]);

  const [columns] = React.useState<DefinedGridProps['columns']>([
    {
      name: 'Flat',
      dataIndex: 'flat',
      minWidth: 180,
      flex: 1,
    },
    {
      name: 'Contacts',
      dataIndex: 'contactTypes',
      minWidth: 310,
      flex: 2,
    },
    {
      name: 'Issues',
      dataIndex: 'issues',
      width: 140,
      textAlign: 'left',
    },
    {
      name: 'Progress',
      dataIndex: 'counts',
      width: 200,
      flex: 2,
    },

    {
      name: 'Evac Support',
      dataIndex: 'contactHasSupportNeeds',
      textAlign: 'left',
      width: 100,
    },
  ]);

  const loadInitialised = React.useRef(!!gridIds.length);

  const handleLoadGrid = React.useCallback(
    (page: 'reset' | 'next') => {
      loadInitialised.current = true;
      dispatch(
        doEngageFlatsLoad({
          propertyId: property.id,
          key: pageKey,
          page,
        }),
      );
    },
    [dispatch, pageKey, property.id],
  );

  const handleLoadMorePress = () => {
    handleLoadGrid('next');
  };

  const handleReloadPress = () => {
    handleLoadGrid('reset');
  };

  const handleOnboardFlatsPress = () => {
    dispatch(doEngageOnboard({ key: pageKey, propertyId: property.id }));
  };

  const handlePressBack = React.useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleRowPress = React.useCallback(
    (flat: EngageFlatListItem) => {
      dispatch(
        doEngageFlatLoad({
          key: pageKey,
          property: property,
          flat: flat,
        }),
      );
    },
    [dispatch, pageKey, property],
  );

  const handleSelectionChange = React.useCallback((ids: string[]) => {
    setSelectedFlatIds(ids);
  }, []);

  const handleBulkSendPress = React.useCallback(() => {
    if (!selectedFlatIds.length) {
      return;
    }
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageSendCommunication,
        params: {
          key: pageKey,
          propertyId: property.id,
          flatIds: selectedFlatIds,
          contactId: null,
          mode: selectedFlatIds.length === 1 ? 'singleFlat' : 'bulkFlat',
        },
      }),
    );
  }, [dispatch, pageKey, property, selectedFlatIds]);

  React.useEffect(() => {
    return () => {
      dispatch(doEngageFlatsClearData({ key: pageKey }));
    };
  }, [dispatch, pageKey]);

  React.useEffect(() => {
    if (!loadInitialised.current) {
      loadInitialised.current = true;
      handleLoadGrid('reset');
    }
    return () => {
      dispatch(doEngageFlatsClearData({ key: pageKey }));
    };
  }, [dispatch, pageKey, handleLoadGrid]);

  return (
    <View style={{ position: 'relative', flex: 1 }}>
      <EngageLoader pageKey={pageKey} />
      <Page
        isInModal={false}
        contentPaddingTop={0}
        ignoreSafeAreaBottom
        header={
          <EngageFlatsHeader
            isLoading={!loadInitialised.current}
            property={property}
            onBackPress={handlePressBack}
            onReloadPress={handleReloadPress}
            onOnboardFlatsPress={handleOnboardFlatsPress}
            flatsCount={totalCount}
            pageKey={pageKey}
            filtersLocked={hasBulkSelection}
            hasBulkSelection={hasBulkSelection}
            onBulkSendPress={handleBulkSendPress}
          />
        }
      >
        {!loadInitialised.current ? (
          <View />
        ) : !gridIds.length ? (
          <PageEmpty
            text={`No Flats Or Contacts Found In\n${property.name}`}
            button={{
              text: 'Onboard Flats',
              onPress: handleOnboardFlatsPress,
            }}
            iconCircled={false}
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : (
          // <PageEmpty
          //   text="No Flats Found"
          //   subtext="Try changing your filter"
          //   button={{
          //     text: 'Onboard Flats',
          //     onPress: () => {},
          //   }}
          //   iconCircled={false}
          //   style={{ backgroundColor: theme.colors.background }}
          // />
          <Grid
            columns={columns}
            data={gridIds}
            showSelectCheckboxes
            selectedIds={selectedFlatIds}
            onSelectionChange={handleSelectionChange}
            extraData={{
              pageKey: pageKey,
              onRowPress: handleRowPress,
            }}
            getRowData={useGetRowData}
            keyExtractor={(i) => `${i}`}
            ListFooterComponent={
              remainingCount > 0 ? (
                <View
                  style={{
                    padding: theme.paddingHorizontal * 2,
                    paddingTop: theme.paddingHorizontal,
                    alignItems: 'flex-start',
                  }}
                >
                  <Button
                    text={pluralise(
                      remainingCount,
                      '{x} More Result, Load',
                      '{x} More Results, Load More',
                    )}
                    onPress={handleLoadMorePress}
                    icon={ICONS.ArrowDownLight}
                  />
                </View>
              ) : (
                <View style={{ height: theme.paddingHorizontal * 2 }} />
              )
            }
            // refreshControl,
          />
        )}
      </Page>
    </View>
  );
};

const styles = StyleSheet.create({
  contactList: {
    gap: 1,
  },
  matchingContactItem: {
    backgroundColor: theme.colors.blue10,
    borderRadius: 6,
    paddingHorizontal: 4,
    marginHorizontal: -4,
  },
  matchingContactName: {
    color: theme.colors.blue,
  },
  expandedContactsEmptyText: {
    color: theme.colors.black,
    marginTop: theme.paddingHorizontalHalf,
  },
});
